/**
 * Drill Templates List Screen
 * View and manage drill templates using DataTable
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import _ from 'lodash'
import {
  Plus,
  Search,
  ListOrdered,
  Grid3x3,
  Shield,
  Pencil,
  Calendar,
  FileText,
} from 'lucide-react'
import React, { useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router'

import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import { drillTimerApi } from '@/services/drillTimerService'
import { formatTimeAndDate } from '@/utils/formatters'

const createColumns = (navigate, entityId) => [
  {
    accessorKey: 'name',
    header: ({ column }) => <SortableColumnHeader column={column} label="Template Name" />,
    cell: ({ row }) => (
      <button
        type="button"
        onClick={() =>
          navigate(`/accounts/${entityId}/drill-timer/templates/${row.original.id}/edit`)
        }
        className="text-left font-medium text-blue-600 hover:underline"
      >
        {row.original.name}
      </button>
    ),
  },
  {
    id: 'types',
    header: () => <span className="text-xs uppercase">Types</span>,
    cell: ({ row }) => {
      const isRescueReady = row.original.is_rescue_ready ?? row.original.isRescueReady
      const isSequential = row.original.is_sequential ?? row.original.isSequential
      const isNonSequential = row.original.is_non_sequential ?? row.original.isNonSequential
      const { sequential } = row.original
      const nonSequential = row.original.non_sequential ?? row.original.nonSequential

      return (
        <div className="flex flex-wrap gap-1">
          {isRescueReady && (
            <Badge variant="outline" className="text-xs">
              <Shield className="mr-1 h-3 w-3" />
              Rescue Ready
            </Badge>
          )}
          {isSequential && (
            <Badge variant="outline" className="text-xs">
              <ListOrdered className="mr-1 h-3 w-3" />
              Sequential
              {sequential?.length > 0 && <span className="ml-1">({sequential.length})</span>}
            </Badge>
          )}
          {isNonSequential && (
            <Badge variant="outline" className="text-xs">
              <Grid3x3 className="mr-1 h-3 w-3" />
              Non-Sequential
              {nonSequential?.length > 0 && <span className="ml-1">({nonSequential.length})</span>}
            </Badge>
          )}
        </div>
      )
    },
  },
  {
    id: 'roles',
    header: () => <span className="text-xs uppercase">Roles</span>,
    cell: ({ row }) => {
      const roles = row.original.roles || []
      if (roles.length === 0) return <span className="text-muted-foreground text-sm">—</span>

      return (
        <div className="flex flex-wrap gap-1">
          {roles.slice(0, 2).map((role) => (
            <Badge key={role} variant="secondary" className="text-xs">
              {role}
            </Badge>
          ))}
          {roles.length > 2 && (
            <Badge variant="secondary" className="text-xs">
              +{roles.length - 2}
            </Badge>
          )}
        </div>
      )
    },
    size: 200,
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => <SortableColumnHeader column={column} label="Created" />,
    cell: ({ row }) => {
      const createdAt = row.original.created_at ?? row.original.createdAt
      if (!createdAt) return <span className="text-muted-foreground">—</span>
      return (
        <div className="flex items-center gap-1.5 text-sm">
          <Calendar className="text-muted-foreground h-3 w-3" />
          <span>{formatTimeAndDate(createdAt)}</span>
        </div>
      )
    },
    size: 190,
  },
  {
    id: 'actions',
    header: () => <span className="text-xs uppercase">Actions</span>,
    cell: ({ row }) => (
      <Button
        className="place-self-end"
        variant="outline"
        size="sm"
        onClick={() => navigate(`${row.original.id}/edit`)}
      >
        <Pencil className="mr-1 h-4 w-4" />
        Edit
      </Button>
    ),
    size: 120,
  },
]

export function DrillTemplatesListScreen() {
  const navigate = useNavigate()
  const { entityId } = useParams()
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })

  // Search shrinks the result set, so a deep page can 404 from the paginator.
  // Reset to page 0 in the same handler so React batches the state updates.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  const { data, isLoading } = useQuery({
    queryKey: ['drillTemplates', entityId, sorting, pagination, searchTerm],
    queryFn: () => {
      const params = {
        q: searchTerm || undefined,
      }

      // Add ordering parameter if sorting is configured
      if (sorting && sorting.length > 0) {
        const sortField = sorting[0]
        params.ordering = sortField.desc ? `-${sortField.id}` : sortField.id
      }

      return drillTimerApi.getTemplates(entityId, params)
    },
    staleTime: 2 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const templates = data?.results || data || []

  const columns = createColumns(navigate, entityId)

  return (
    <Layout
      breadcrumbs={[
        { pathname: `/accounts/${entityId}/drill-timer`, title: 'Drill Timer' },
        'Templates',
      ]}
    >
      <Layout.Heading title="Drill Templates">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('..')}>
            Back to Drill Logs
          </Button>
          <Button onClick={() => navigate('new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Template
          </Button>
        </div>
      </Layout.Heading>

      {/* Search and Filters */}
      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <div className="relative w-full md:w-64">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform" />
            <Input
              type="text"
              placeholder="Search templates..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                debounceSearch(e.target.value)
              }}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* DataTable or Empty State */}
      {(() => {
        if (!isLoading && templates.length === 0) {
          return (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No Drill Templates Available</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  Get started by creating your first drill template
                </p>
                <Button onClick={() => navigate('new')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create First Template
                </Button>
              </CardContent>
            </Card>
          )
        }

        if (!isLoading && templates.length === 0 && search) {
          return (
            <Card>
              <CardContent className="py-12 text-center">
                <Search className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No Results Found</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  No templates match &quot;{search}&quot;. Try adjusting your search.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('')
                    setSearchTerm('')
                    resetToFirstPage()
                  }}
                >
                  Clear Search
                </Button>
              </CardContent>
            </Card>
          )
        }

        return (
          <DataTable
            columns={columns}
            data={templates}
            loading={isLoading}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            rowCount={templates.length}
          />
        )
      })()}
    </Layout>
  )
}
